import { z } from 'zod/v4';
import {
  TQLHILinkDependDTO,
  TQLHILinkTypeDTO,
} from '@/entities/monitoring/hi/@x/monitoring-template';
import { TQLFilterDTO } from '@/entities/srm/tql-query/@x/monitoring-template';
import {
  VMAgentItemDTO,
  VMAgentJobDTO,
} from '@/entities/monitoring/vm-agent/@x/monitoring-template';

/* -------------------- Base Schemas -------------------- */

export const MonitoringTemplateDTO = z.object({
  id: z.string(),
  label: z.string(),
  name: z.string(),
  description: z.string(),
  query: z.any().optional(),
  filter: z.any().optional(),
  enabled: z.boolean(),
});
export type MonitoringTemplate = z.infer<typeof MonitoringTemplateDTO>;

export const MonitoringTemplateItemDTO = MonitoringTemplateDTO.pick({
  id: true,
  name: true,
  label: true,
});

export type MonitoringTemplateItem = z.infer<typeof MonitoringTemplateItemDTO>;

export const CopyMonitoringTemplateRequestDTO = z.object({
  name: z.string(),
  label: z.string(),
});
export type CopyMonitoringTemplateRequest = z.infer<
  typeof CopyMonitoringTemplateRequestDTO
>;

export const CopyMonitoringTemplateResponseDTO = z.object({
  id: z.string(),
  name: z.string(),
  label: z.string(),
  description: z.string().nullable(),
  query: z.record(z.string(), z.any()),
});
export type CopyMonitoringTemplateResponse = z.infer<
  typeof CopyMonitoringTemplateResponseDTO
>;

export const CreateMonitoringTemplateRequestDTO = z.object({
  name: z.string(),
  description: z.string(),
  query: z.record(z.string(), z.any()),
});
export type CreateMonitoringTemplateRequest = z.infer<
  typeof CreateMonitoringTemplateRequestDTO
>;

export const UpdateMonitoringTemplateRequestDTO =
  CreateMonitoringTemplateRequestDTO.omit({ name: true }).partial();

export type UpdateMonitoringTemplateRequest = z.infer<
  typeof UpdateMonitoringTemplateRequestDTO
>;

/* -------------------- Metrics -------------------- */

export const MonitoringTemplateMetricV4DTO = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  template: z.string(),
  enabled: z.boolean(),
  monitoring_template_name: z.string(),
  template_label: z.string().optional(),
  node_id: z.string(),
  vmagent_jobs: z.array(
    z.object({
      id: z.string(),
      vmagent: VMAgentItemDTO.extend({ tags: z.array(z.string()) }),
      job: VMAgentJobDTO.extend({ tags: z.array(z.string()) }),
    }),
  ),
  targets_count: z.number().optional(),
  failed_targets_count: z.number().optional(),
});

export type MonitoringTemplateMetricV4 = z.infer<
  typeof MonitoringTemplateMetricV4DTO
>;

export const MonitoringTemplateMetricDTO = z.object({
  id: z.string(),
  name: z.string(),
  job_name: z.string(),
  template: z.string(),
  job_id: z.string().optional(),
  vmagent_jobs_ids: z.array(z.string()).optional(),
  enabled: z.boolean(),
  monitoring_template_name: z.string(),
  node_id: z.number(),
  targets_count: z.number().optional(),
  description: z.string().optional(),
});
export type MonitoringTemplateMetric = z.infer<
  typeof MonitoringTemplateMetricDTO
>;

export const CreateMonitoringTemplateMetricDTO = z.object({
  name: z.string(),
  template: z.string(),
  vmagent_jobs_ids: z.array(z.string()),
  monitoring_template_name: z.string(),
  node_id: z.string(),
  description: z.string().optional(),
});
export type CreateMonitoringTemplateMetric = z.infer<
  typeof CreateMonitoringTemplateMetricDTO
>;

export const UpdateMonitoringTemplateMetricDTO = z.object({
  template: z.string(),
  vmagent_jobs_ids: z.array(z.string()),
  description: z.string().optional(),
  node_id: z.string(),
});
export type UpdateMonitoringTemplateMetric = z.infer<
  typeof UpdateMonitoringTemplateMetricDTO
>;

/* -------------------- HITs -------------------- */

export const MonitoringTemplateHITDTO = z.object({
  hi_type_name: z.string(),
  template_name: z.string(),
  metric_name: z.string(),
  type: TQLHILinkTypeDTO,
  expression: z.number(),
  vmalert_names: z.array(z.string()).optional(),
  depend_hi_link_ids: z.array(TQLHILinkDependDTO),
  rule: z.string(),
  event_path: z.string().optional(),
  node_id: z.number(),
  arguments: z.record(z.string(), z.any()),
  enabled: z.boolean(),
});
export type MonitoringTemplateHIT = z.infer<typeof MonitoringTemplateHITDTO>;

export const MonitoringTemplateHITResponseDTO = MonitoringTemplateHITDTO.extend(
  {
    id: z.string(),
    hi_type_label: z.string(),
    his_count: z.number().optional(),
    failed_hints_count: z.number().optional(),
  },
);
export type MonitoringTemplateHITResponse = z.infer<
  typeof MonitoringTemplateHITResponseDTO
>;

/* -------------------- HIT Depends -------------------- */

export const HITDependsDTO = z.object({
  hi_type_name: z.string(),
  hi_type_label: z.string(),
  link_id: z.string(),
  src_node_id: z.string().optional(),
  templates: z.array(
    z.object({
      template_name: z.string(),
      template_label: z.string(),
      query: TQLFilterDTO,
      tql_hi_links: z.array(
        z.object({
          origin_node_id: z.string(),
          hi_link_id: z.string(),
          node_id: z.string(),
        }),
      ),
    }),
  ),
});
export type HITDepends = z.infer<typeof HITDependsDTO>;

/* -------------------- Export DTO -------------------- */

export const MonitoringTemplateExportDTO = z.object({
  label: z.string(),
  description: z.string(),
  query: z.record(z.string(), z.any()),
  name: z.string(),
  tql_metrics: z.array(MonitoringTemplateMetricDTO),
  tql_hi_links: z.array(MonitoringTemplateHITDTO),
  import: z.boolean(),
  force: z.boolean(),
});
export type MonitoringTemplateExport = z.infer<
  typeof MonitoringTemplateExportDTO
>;

/* -------------------- ErrorField and WithError -------------------- */

export const ErrorFieldDTO = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    fault_entity: z.any(), // In recursive  case
  }),
});
export type ErrorField = z.infer<typeof ErrorFieldDTO>;

export const MonitoringTemplateImportDTO = z.object({
  error: z.array(
    z.object({
      code: z.string(),
      message: z.string(),
      fault_entity: z.union([MonitoringTemplateExportDTO, ErrorFieldDTO]),
    }),
  ),
});
export type MonitoringTemplateImport = z.infer<
  typeof MonitoringTemplateImportDTO
>;
